import React, { createContext, ReactNode, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import AuthService from '../Api_Module/Api_Services/AuthService';
import { ResearchDataState, SwotAnalysisState, TamAnalysisState } from '../interface/ReasearchData';
import { setResearchData } from '../store/ResearchDataSlice';
import { alertWarningMessage } from '../utility/CustomAlertMessage';

interface UserData {
    id?: string;
    email?: string;
    name?: string;
    subscriptionStatus?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    daysLeft?: number;
    stripeSubscriptionId?: string | null;
    stripeCustomerId?: string | null;
    referralDiscountPercentage?: number;
    shareIncomePercentage?: number;
    appliedMonthDiscount?: number | null;
    appliedYearDiscount?: number | null;
    isTrial?: boolean;
    planActive?: boolean;
    product?: string;
    roles?: string[];
    planInterval?: string | null;
    amountTotal?: number;
    currency?: string;
    receiptNumber?: string;
    status?: string;
}

interface UserContextType {
    researchdata: ResearchDataState;
    loading: boolean;
    userData: UserData | null;
    trialDaysRemaining: number | null;
    isTrialUser: boolean;
    popupText?: string;
    getResearchData: (symbol: string) => Promise<void>;
    refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const UserProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch();
    const researchdata = useSelector((state: any) => state.researchData) as ResearchDataState;
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
    const [isTrialUser, setIsTrialUser] = useState<boolean>(false);
    const [popup, setPopup] = useState<string>('');

    // Calculate trial days remaining
    const calculateTrialDays = (trialStartDate: string) => {
        const startDate = new Date(trialStartDate);
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - startDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const remainingDays = Math.max(0, 14 - diffDays); // Assuming 14-day trial
        return remainingDays;
    };

    const getResearchData = async (symbol: string) => {
        try {
            if (!symbol) {
                console.warn('Symbol is required to fetch research data');
                return;
            }
            const result = await AuthService.getStockResearchData(symbol);

            if (result && result != "") {
                dispatch(setResearchData(result.payload));
            } else {
                const initialState: ResearchDataState = {
                    swotAnalysis: {} as SwotAnalysisState,
                    tamAnalysis: {} as TamAnalysisState,
                    scenario: [],
                    lastScenario: {
                        name: 'Undefined Scenario',
                        calcBase: 'prior',
                        bull: {
                            data: [],
                            thirdYearProjectionLabel: '',
                            thirdYearProjectionSharePriceLow: '$0',
                            thirdYearProjectionSharePriceHigh: '$0',
                            thirdYearProjectionCGR: '0%-0%',
                            fifthYearProjectionLabel: '',
                            fifthYearProjectionSharePriceLow: '$0',
                            fifthYearProjectionSharePriceHigh: '$0',
                            fifthYearProjectionCGR: '0%-0%',
                        },
                        base: {
                            data: [],
                            thirdYearProjectionLabel: '',
                            thirdYearProjectionSharePriceLow: '$0',
                            thirdYearProjectionSharePriceHigh: '$0',
                            thirdYearProjectionCGR: '0%-0%',
                            fifthYearProjectionLabel: '',
                            fifthYearProjectionSharePriceLow: '$0',
                            fifthYearProjectionSharePriceHigh: '$0',
                            fifthYearProjectionCGR: '0%-0%',
                        },
                        bear: {
                            data: [],
                            thirdYearProjectionLabel: '',
                            thirdYearProjectionSharePriceLow: '$0',
                            thirdYearProjectionSharePriceHigh: '$0',
                            thirdYearProjectionCGR: '0%-0%',
                            fifthYearProjectionLabel: '',
                            fifthYearProjectionSharePriceLow: '$0',
                            fifthYearProjectionSharePriceHigh: '$0',
                            fifthYearProjectionCGR: '0%-0%',
                        },
                    },
                    saved: {}
                };
                dispatch(setResearchData(initialState));
                console.warn('No data received for symbol:', symbol);
            }
            setLoading(true)
        } catch (error) {
            console.error('Failed to fetch research data:', error);
            alertWarningMessage('Failed to load research data. Please try again.');
            setLoading(true)
        }
    };

    const refreshUser = async () => {
        const token = sessionStorage.getItem('STOCK_AUTH_TOKEN');
        if (!token) {
            setUserData(null);
            setTrialDaysRemaining(null);
            setIsTrialUser(false);
            setPopup('nopopup');
            return;
        }

        try {
            const decoded = JSON.parse(atob(token.split('.')[1]));
            const isTokenValid = decoded.exp > Date.now() / 1000;

            if (isTokenValid) {
                const fetchedUserData = await AuthService.getUserData();
                if (fetchedUserData) {
                    setUserData(fetchedUserData);
                    // Set trial information based on actual userData structure
                    if (fetchedUserData?.subscriptionStatus === 'trialing' &&
                        fetchedUserData?.planActive === true &&
                        fetchedUserData?.isTrail === true) {
                        setTrialDaysRemaining(fetchedUserData.daysLeft || 0);
                        setIsTrialUser(true);
                    } else {
                        setTrialDaysRemaining(null);
                        setIsTrialUser(false);
                    }
                    setPopup('popup');
                } else {
                    setUserData(null);
                    setPopup('nopopup');
                    sessionStorage.clear();
                    window.location.reload();
                }
            } else {
                sessionStorage.removeItem('STOCK_AUTH_TOKEN');
                setUserData(null);
                setPopup('nopopup');
                setTrialDaysRemaining(null);
                setIsTrialUser(false);
            }
        } catch (error) {
            console.error('Error refreshing user:', error);
            setUserData(null);
            setPopup('nopopup');
            sessionStorage.clear();
            window.location.reload();
        }
    };

    // Automatically refresh user data on mount and when auth token changes
    useEffect(() => {
        refreshUser();
    }, []); // Run on mount

    // Also refresh when auth token changes in session storage
    useEffect(() => {
        const handleStorageChange = () => {
            refreshUser();
        };

        // Listen for storage changes (when auth token is added/removed)
        window.addEventListener('storage', handleStorageChange);

        // Also check periodically in case of same-tab changes
        const interval = setInterval(() => {
            const currentToken = sessionStorage.getItem('STOCK_AUTH_TOKEN');
            const hasToken = !!currentToken;
            const hadUserData = !!userData;

            // Refresh if token state changed
            if ((hasToken && !hadUserData) || (!hasToken && hadUserData)) {
                refreshUser();
            }
        }, 8000); // Increased interval to 8 seconds to reduce frequency

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [userData, refreshUser]); // Added refreshUser to dependencies
    return (
        <UserContext.Provider value={{
            researchdata,
            loading,
            userData,
            trialDaysRemaining,
            isTrialUser,
            getResearchData,
            refreshUser,
            popupText: popup
        }}>
            {children}
        </UserContext.Provider>
    );
};


// Custom hook for using the user context
const useUser = () => {
    const context = React.useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return { user: context.userData, ...context };
};

export { UserContext, UserProvider, useUser };